'use client';

import { useEffect, useRef } from 'react';
import { getCurrentVfxTier } from '@/utils/vfxManager';
import styles from './PublicProfilePortrait.module.css';

type PublicProfilePortraitProps = {
  avatarUrl: string;
  displayName: string;
  topics: Array<{ id: string; label: string; description: string }>;
  controlsId?: string;
  selectedTopic: string | null;
  activeTopic: string | null;
  onTopicSelect: (id: string) => void;
  onTopicHover: (id: string | null) => void;
  motionEnabled: boolean;
};

const DEFAULT_AVATAR = '/uploads/chat_room_avatars/lisad_default_avatar.gif';

function createParticles() {
  let seed = 7319;
  const random = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  return Array.from({ length: 28 }, (_, index) => ({
    angle: (index / 28) * Math.PI * 2 + (random() - 0.5) * 0.18,
    radius: 0.82 + random() * 0.18,
    size: 0.7 + random() * 1.1,
    phase: random() * Math.PI * 2,
  }));
}

export function PublicProfilePortrait({ avatarUrl, displayName, topics, controlsId, selectedTopic, activeTopic, onTopicSelect, onTopicHover, motionEnabled }: PublicProfilePortraitProps) {
  const sceneRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const redrawRef = useRef<(() => void) | null>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const connectionRef = useRef<SVGPathElement>(null);
  const topicRefs = useRef(new Map<string, HTMLButtonElement>());
  const hoveredTopicRef = useRef<string | null>(null);
  const focusedTopicRef = useRef<string | null>(null);
  const activeTopicRef = useRef<string | null>(null);
  const visibleTopics = topics.slice(0, 3);
  const visibleActiveTopic = visibleTopics.some(topic => topic.id === activeTopic) ? activeTopic : null;
  const visibleSelectedTopic = visibleTopics.some(topic => topic.id === selectedTopic) ? selectedTopic : null;

  function previewTopic(source: 'pointer' | 'focus', id: string | null) {
    if (source === 'pointer') hoveredTopicRef.current = id;
    else focusedTopicRef.current = id;
    onTopicHover(id ?? (source === 'pointer' ? focusedTopicRef.current : hoveredTopicRef.current));
  }

  useEffect(() => {
    const ids = new Set(topics.slice(0, 3).map(topic => topic.id));
    const invalidHover = hoveredTopicRef.current !== null && !ids.has(hoveredTopicRef.current);
    const invalidFocus = focusedTopicRef.current !== null && !ids.has(focusedTopicRef.current);
    if (invalidHover) hoveredTopicRef.current = null;
    if (invalidFocus) focusedTopicRef.current = null;
    if (invalidHover || invalidFocus) onTopicHover(focusedTopicRef.current ?? hoveredTopicRef.current);
  }, [topics, onTopicHover]);

  useEffect(() => {
    activeTopicRef.current = visibleActiveTopic;
    redrawRef.current?.();
  }, [visibleActiveTopic, topics]);

  useEffect(() => {
    const scene = sceneRef.current;
    const canvas = canvasRef.current;
    if (!scene || !canvas) return;
    const context = canvas.getContext('2d');

    const particles = createParticles();
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const smallScreen = window.matchMedia('(max-width: 700px)');
    const pointer = { x: 0, y: 0, inside: false };
    let tier = getCurrentVfxTier() ?? 'medium';
    let width = 0;
    let height = 0;
    let frame: number | null = null;
    let previousTime: number | null = null;
    let elapsed = 0;
    let disposed = false;
    const initialRect = scene.getBoundingClientRect();
    let visible = initialRect.bottom > 0 && initialRect.top < window.innerHeight;

    const motionAllowed = () => motionEnabled && tier !== 'off' && !reducedMotion.matches && !smallScreen.matches && finePointer.matches;
    const shouldAnimate = () => motionAllowed() && visible && !document.hidden && !disposed;
    const resetTilt = () => {
      pointer.inside = false;
      scene.style.setProperty('--tilt-x', '0deg');
      scene.style.setProperty('--tilt-y', '0deg');
      scene.style.setProperty('--depth-shift', '0px');
      scene.style.setProperty('--light-x', '50%');
      scene.style.setProperty('--light-y', '50%');
    };

    const drawConnection = () => {
      const path = connectionRef.current;
      const frameElement = frameRef.current;
      const topicElement = activeTopicRef.current ? topicRefs.current.get(activeTopicRef.current) : null;
      if (!path) return;
      if (disposed || smallScreen.matches || !visible || document.hidden || !frameElement || !topicElement) {
        path.setAttribute('d', '');
        return;
      }
      const sceneRect = scene.getBoundingClientRect();
      const frameRect = frameElement.getBoundingClientRect();
      const topicRect = topicElement.getBoundingClientRect();
      if (!sceneRect.width || !sceneRect.height || !frameRect.width || !topicRect.width) {
        path.setAttribute('d', '');
        return;
      }
      const startX = topicRect.left + topicRect.width / 2 - sceneRect.left;
      const startY = topicRect.top + topicRect.height / 2 - sceneRect.top;
      const frameCenter = frameRect.left + frameRect.width / 2 - sceneRect.left;
      const endX = (startX < frameCenter ? frameRect.left : frameRect.right) - sceneRect.left;
      const endY = Math.max(frameRect.top + 24, Math.min(frameRect.bottom - 24, topicRect.top + topicRect.height / 2)) - sceneRect.top;
      const bendX = (startX + endX) / 2;
      path.ownerSVGElement?.setAttribute('viewBox', `0 0 ${sceneRect.width} ${sceneRect.height}`);
      path.setAttribute('d', `M ${startX} ${startY} C ${bendX} ${startY}, ${bendX} ${endY}, ${endX} ${endY}`);
    };
    const draw = () => {
      drawConnection();
      if (disposed || !context || !width || !height) return;
      context.clearRect(0, 0, width, height);
      if (!motionAllowed()) return;
      const hasPreview = activeTopicRef.current !== null;
      const color = '88, 211, 211';
      const points = particles.filter((_, index) => tier === 'max' || index % 2 === 0).map(particle => {
        const drift = Math.sin(elapsed * 0.00035 + particle.phase) * 0.012;
        let x = width * (0.5 + Math.cos(particle.angle + drift) * 0.46 * particle.radius);
        let y = height * (0.47 + Math.sin(particle.angle + drift) * 0.39 * particle.radius);
        const distance = Math.hypot(x - pointer.x, y - pointer.y);
        const near = pointer.inside && distance < 130;
        if (near) {
          const influence = (1 - distance / 130) * 0.06;
          x += (pointer.x - x) * influence;
          y += (pointer.y - y) * influence;
        }
        return { x, y, size: particle.size, near };
      });

      const maxDistance = Math.min(width * 0.25, 160);
      context.lineWidth = 0.6;
      context.shadowBlur = 0;
      for (let index = 0; index < points.length; index += 1) {
        const point = points[index];
        for (let next = index + 1; next < points.length; next += 1) {
          const other = points[next];
          const distance = Math.hypot(point.x - other.x, point.y - other.y);
          if (distance > maxDistance) continue;
          context.strokeStyle = `rgba(${color}, ${(1 - distance / maxDistance) * (hasPreview || point.near ? 0.42 : 0.23)})`;
          context.beginPath();
          context.moveTo(point.x, point.y);
          context.lineTo(other.x, other.y);
          context.stroke();
        }
      }
      for (const point of points) {
        context.fillStyle = `rgba(${color}, ${point.near || hasPreview ? 0.8 : 0.48})`;
        context.shadowColor = `rgba(${color}, 0.35)`;
        context.shadowBlur = tier === 'max' ? 4 : 0;
        context.beginPath();
        context.arc(point.x, point.y, point.size, 0, Math.PI * 2);
        context.fill();
      }
      context.shadowBlur = 0;
    };

    const tick = (timestamp: number) => {
      frame = null;
      if (!shouldAnimate()) return;
      if (previousTime !== null) elapsed += Math.min(timestamp - previousTime, 50);
      previousTime = timestamp;
      draw();
      frame = window.requestAnimationFrame(tick);
    };
    const updateAnimation = () => {
      scene.dataset.motion = motionAllowed() ? 'on' : 'off';
      if (shouldAnimate()) {
        if (frame === null) frame = window.requestAnimationFrame(tick);
      } else {
        if (frame !== null) window.cancelAnimationFrame(frame);
        frame = null;
        previousTime = null;
        resetTilt();
      }
      draw();
    };
    const updateTier = () => {
      tier = getCurrentVfxTier() ?? 'medium';
      resize();
    };
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, tier === 'max' ? 1.5 : 1);
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      context?.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      updateAnimation();
    };
    const movePointer = (event: PointerEvent) => {
      if (!shouldAnimate() || !finePointer.matches || event.pointerType === 'touch') return;
      const rect = scene.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width) * 2 - 1));
      const y = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height) * 2 - 1));
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
      pointer.inside = true;
      scene.style.setProperty('--tilt-x', `${(-y * 3).toFixed(2)}deg`);
      scene.style.setProperty('--tilt-y', `${(x * 3).toFixed(2)}deg`);
      scene.style.setProperty('--depth-shift', `${(x * 6).toFixed(2)}px`);
      scene.style.setProperty('--light-x', `${((x + 1) * 50).toFixed(1)}%`);
      scene.style.setProperty('--light-y', `${((y + 1) * 50).toFixed(1)}%`);
    };

    const visibilityObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      updateAnimation();
    });
    const resizeObserver = new ResizeObserver(resize);
    const tierObserver = new MutationObserver(updateTier);
    tierObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-vfx'] });
    visibilityObserver.observe(scene);
    resizeObserver.observe(canvas);
    scene.addEventListener('pointermove', movePointer, { passive: true });
    scene.addEventListener('pointerleave', resetTilt);
    scene.addEventListener('pointercancel', resetTilt);
    finePointer.addEventListener('change', updateAnimation);
    reducedMotion.addEventListener('change', updateAnimation);
    smallScreen.addEventListener('change', updateAnimation);
    document.addEventListener('visibilitychange', updateAnimation);
    redrawRef.current = draw;
    resetTilt();
    resize();

    return () => {
      disposed = true;
      if (frame !== null) window.cancelAnimationFrame(frame);
      redrawRef.current = null;
      visibilityObserver.disconnect();
      resizeObserver.disconnect();
      tierObserver.disconnect();
      scene.removeEventListener('pointermove', movePointer);
      scene.removeEventListener('pointerleave', resetTilt);
      scene.removeEventListener('pointercancel', resetTilt);
      finePointer.removeEventListener('change', updateAnimation);
      reducedMotion.removeEventListener('change', updateAnimation);
      smallScreen.removeEventListener('change', updateAnimation);
      document.removeEventListener('visibilitychange', updateAnimation);
      resetTilt();
      connectionRef.current?.setAttribute('d', '');
    };
  }, [motionEnabled]);

  return (
    <div ref={sceneRef} className={styles.scene} data-motion={motionEnabled ? 'on' : 'off'} data-active-topic={visibleActiveTopic || undefined} data-selected-topic={visibleSelectedTopic || undefined}>
      <canvas ref={canvasRef} className={styles.constellation} aria-hidden="true" />
      <svg className={styles.connection} aria-hidden="true" focusable="false"><path ref={connectionRef} /></svg>
      <div className={styles.stack}>
        <div className={`${styles.back} ${styles.backTwo}`} aria-hidden="true" />
        <div className={`${styles.back} ${styles.backOne}`} aria-hidden="true" />
        <div className={styles.photo}>
          <img key={avatarUrl} src={avatarUrl || DEFAULT_AVATAR} alt={`Фотография: ${displayName}`} draggable={false} onError={event => {
            if (event.currentTarget.getAttribute('src') !== DEFAULT_AVATAR) event.currentTarget.src = DEFAULT_AVATAR;
          }} />
        </div>
        <div ref={frameRef} className={styles.front} aria-hidden="true" />
      </div>
      <div className={styles.topicList}>
      {visibleTopics.map((topic) => (
        <button key={topic.id} ref={element => { if (element) topicRefs.current.set(topic.id, element); else topicRefs.current.delete(topic.id); }} type="button" className={`${styles.topic} ${visibleActiveTopic === topic.id ? styles.active : ''} ${visibleSelectedTopic === topic.id ? styles.selected : ''}`} aria-pressed={selectedTopic === topic.id} aria-controls={controlsId ?? topic.id} aria-description={topic.description || undefined} onPointerEnter={() => previewTopic('pointer', topic.id)} onPointerLeave={() => previewTopic('pointer', null)} onFocus={() => previewTopic('focus', topic.id)} onBlur={() => previewTopic('focus', null)} onClick={() => onTopicSelect(topic.id)}>
          {topic.label}
        </button>
      ))}
      </div>
      {visibleTopics.length > 0 && <div className={styles.caption} aria-live="polite">
        {/* Все варианты занимают одну ячейку: наведение не меняет высоту сцены и положение кнопок. */}
        <p aria-hidden={Boolean(visibleActiveTopic)}>Выберите направление</p>
        {visibleTopics.map(topic => <p key={topic.id} aria-hidden={visibleActiveTopic !== topic.id}><strong>{topic.label}</strong><br />{topic.description || 'Связанные материалы выделены ниже.'}</p>)}
      </div>}
    </div>
  );
}
